import React, { useCallback, useEffect, useState } from "react";
import { Button, Empty, Image, Modal, Space, Spin, Tooltip } from "antd";
import { DownloadOutlined, EyeOutlined } from "@ant-design/icons";
import DocViewer, { DocViewerRenderers } from "react-doc-viewer";
import { api } from "@/redux/api";
import message from "@/utils/globalMessage";

const IMAGE_EXTS = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg", "ico"];
const DOC_EXTS = ["doc", "docx", "ppt", "pptx", "xls", "xlsx"];
const MEDIA_EXTS = ["mp4", "mov", "avi", "flv", "rmvb", "wmv", "webm", "mkv", "m4v"];
const AUDIO_EXTS = ["mp3", "wav", "flac", "aac", "ogg", "wma", "m4a"];
const IFRAME_EXTS = ["pdf", "txt", "text", "json", "html", "htm", "xml", "md", "csv", "log"];

// 从文件原文件名（或 URL 最后一段）提取小写扩展名
const getExt = (name = "") => {
  const last = String(name).split("?")[0].split("#")[0];
  const fileName = last.split("/").pop();
  const dot = fileName.lastIndexOf(".");
  if (dot <= 0 || dot === fileName.length - 1) return "";
  return fileName.slice(dot + 1).toLowerCase();
};

// 带 query 的签名 URL（末尾 .ext?X-Amz-...）必须从原始文件名判断类型，
// 不能依赖 FileManagement/utils 里基于 URL 路径的正则（对纯文件名会判不出类型）。
const hasExt = (fileName, list) => list.includes(getExt(fileName));
const isImageFile = (fileName) => hasExt(fileName, IMAGE_EXTS);
const isDocFile = (fileName) => hasExt(fileName, DOC_EXTS);
const isMediaFile = (fileName) => hasExt(fileName, MEDIA_EXTS);
const isAudioFile = (fileName) => hasExt(fileName, AUDIO_EXTS);
const isIframeFile = (fileName) => hasExt(fileName, IFRAME_EXTS);

const DOC_FILE_TYPE = {
  doc: "docx",
  docx: "docx",
  xls: "xlsx",
  xlsx: "xlsx",
  ppt: "pptx",
  pptx: "pptx",
};

/**
 * 附件「预览 / 下载」通用组件。
 *
 * 预览/下载逻辑对齐 /facility/:company/:facility/file-manage 的文件预览：
 *   - 附件存放在私有 S3 bucket（PrivateMediaStorage, acl=private），不能直接访问；
 *     必须把原始对象 key（file_key）交给后端 generate_url(key, is_preview=true/false)
 *     换取签名 URL。
 *   - 预览用 inline 签名 URL，按文件类型渲染（图片/PDF/文本 iframe / Office / 音视频）。
 *   - 下载用 attachment 签名 URL。
 *
 * @param {object} attachment 形如 { id, url, file_key, original_name, content_type, size }
 * @param {'text'|'icon'} [variant] 展示形态（默认 text 显示文件名）
 */
const AttachmentViewer = ({ attachment, variant = "text" }) => {
  const fileKey = attachment?.file_key || attachment?.key;
  const rawName =
    attachment?.original_name || attachment?.name || "";
  const fileName = rawName || (attachment?.url ? attachment.url.split("?")[0].split("/").pop() : "");
  const displayName = fileName || "Attachment";

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState("");
  const [loading, setLoading] = useState(false);

  // 下载：attachment 语义签名 URL → 触发下载
  const handleDownload = useCallback(async () => {
    setLoading(true);
    try {
      if (fileKey) {
        const res = await api.generate_url(fileKey, false);
        const src = res?.data?.url;
        if (!src) throw new Error("No download url");
        const link = document.createElement("a");
        link.href = src;
        link.setAttribute("download", displayName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (attachment?.url) {
        const link = document.createElement("a");
        link.href = attachment.url;
        link.setAttribute("download", displayName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        message.error("Attachment is not available");
      }
    } catch (e) {
      message.error("Failed to download attachment");
    } finally {
      setLoading(false);
    }
  }, [fileKey, attachment, displayName]);

  // 预览：获取 inline 签名 URL
  const openPreview = useCallback(async () => {
    if (!fileKey && !attachment?.url) {
      message.error("Attachment is not available");
      return;
    }
    setLoading(true);
    try {
      let src = attachment?.url;
      if (fileKey) {
        const res = await api.generate_url(fileKey, true);
        src = res?.data?.url || src;
      }
      setPreviewSrc(src);
      setPreviewOpen(true);
    } catch (e) {
      message.error("Failed to load preview");
    } finally {
      setLoading(false);
    }
  }, [fileKey, attachment]);

  useEffect(() => () => {
    if (previewSrc) URL.revokeObjectURL(previewSrc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewSrc]);

  const renderPreview = () => {
    if (!previewSrc) {
      return (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      );
    }
    const ext = getExt(fileName);
    if (isImageFile(fileName)) {
      return (
        <Image
          style={{ width: "100%" }}
          src={previewSrc}
          alt={displayName}
          preview={false}
        />
      );
    }
    if (isDocFile(fileName)) {
      // Office 预览：签名 URL 带 query，react-doc-viewer 无法从 uri 推断类型，需显式 fileType
      return (
        <DocViewer
          pluginRenderers={DocViewerRenderers}
          style={{ width: "100%", height: "72vh" }}
          prefetchMethod="GET"
          documents={[
            { uri: previewSrc, fileType: DOC_FILE_TYPE[ext] || ext, fileName: displayName },
          ]}
        />
      );
    }
    if (isMediaFile(fileName)) {
      return (
        <video style={{ width: "100%", maxHeight: "72vh" }} src={previewSrc} controls />
      );
    }
    if (isAudioFile(fileName)) {
      return <audio style={{ width: "100%" }} src={previewSrc} controls />;
    }
    if (isIframeFile(fileName)) {
      return (
        <iframe
          title={displayName}
          src={previewSrc}
          style={{ width: "100%", height: "72vh", border: 0 }}
        />
      );
    }
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={`No inline preview for ".${ext || "unknown"}" file.`}
      />
    );
  };

  const actions = (withPreviewText) => (
    <>
      <Button
        type="link"
        size="small"
        icon={<EyeOutlined />}
        loading={loading}
        onClick={openPreview}
        disabled={!fileKey && !attachment?.url}
        title="Preview attachment"
        style={{ padding: 0 }}
      >
        {withPreviewText ? displayName : null}
      </Button>
      <Tooltip title="Download">
        <Button
          type="text"
          size="small"
          icon={<DownloadOutlined />}
          loading={loading}
          onClick={handleDownload}
          disabled={!fileKey && !attachment?.url}
        />
      </Tooltip>
    </>
  );

  return (
    <>
      <Space size={4} style={{ display: "inline-flex" }}>
        {actions(variant !== "icon")}
      </Space>
      <Modal
        title={displayName}
        open={previewOpen}
        footer={null}
        width="min(960px, 92vw)"
        destroyOnClose
        onCancel={() => setPreviewOpen(false)}
      >
        {renderPreview()}
      </Modal>
    </>
  );
};

export default AttachmentViewer;
